import { useState } from 'react';
import { Copy, KeyRound, Link2, LogOut, Mail, ShieldCheck } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui';
import { useMe } from '@/lib/queries';
import { t } from '@/lib/i18n';

/**
 * Account utilities: send yourself a test email (proves mail delivery) and —
 * for admins — mint an instant-share link that logs a recipient straight in.
 */
export function AccountPage() {
  const { data: me } = useMe();
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busyShare, setBusyShare] = useState(false);
  const [busyTest, setBusyTest] = useState(false);
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [busyPw, setBusyPw] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busySignOut, setBusySignOut] = useState(false);
  const [signOutMsg, setSignOutMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function makeShareLink() {
    setBusyShare(true);
    setShareLink(null);
    setCopied(false);
    try {
      const { link } = await api.createShareLink();
      setShareLink(link);
    } catch (err) {
      setShareLink(null);
      setTestMsg({ ok: false, text: err instanceof ApiError ? err.message : t('accountPage.failedToCreate') });
    } finally {
      setBusyShare(false);
    }
  }

  async function copy() {
    if (!shareLink) return;
    await navigator.clipboard.writeText(shareLink).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    if (newPw !== confirmPw) {
      setPwMsg({ ok: false, text: t('accountPage.passwordsDoNotMatch') });
      return;
    }
    setBusyPw(true);
    setPwMsg(null);
    try {
      const res = await api.changePassword(currentPw, newPw);
      const sessionsTxt = res.sessions === 1 ? t('accountPage.oneOtherSession') : t('accountPage.nOtherSessions', { n: res.sessions });
      const devicesTxt = res.devices === 1 ? t('accountPage.oneRememberedDevice') : t('accountPage.nRememberedDevices', { n: res.devices });
      setPwMsg({
        ok: true,
        text: t('accountPage.passwordChanged', { sessions: sessionsTxt, devices: devicesTxt }),
      });
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
    } catch (err) {
      setPwMsg({ ok: false, text: err instanceof ApiError ? err.message : t('accountPage.couldNotChangePassword') });
    } finally {
      setBusyPw(false);
    }
  }

  async function signOutEverywhere() {
    setBusySignOut(true);
    setSignOutMsg(null);
    try {
      const res = await api.signOutEverywhere();
      const sessionsTxt = res.sessions === 1 ? t('accountPage.oneOtherSession') : t('accountPage.nOtherSessions', { n: res.sessions });
      const devicesTxt = res.devices === 1 ? t('accountPage.oneRememberedDevice') : t('accountPage.nRememberedDevices', { n: res.devices });
      setSignOutMsg({
        ok: true,
        text: t('accountPage.signedOutOthers', { sessions: sessionsTxt, devices: devicesTxt }),
      });
    } catch (err) {
      setSignOutMsg({ ok: false, text: err instanceof ApiError ? err.message : t('accountPage.couldNotSignOutOthers') });
    } finally {
      setBusySignOut(false);
    }
  }

  async function sendTest() {
    setBusyTest(true);
    setTestMsg(null);
    try {
      await api.sendTestEmail();
      setTestMsg({ ok: true, text: t('accountPage.testEmailSent', { email: me?.email ?? t('accountPage.you') }) });
    } catch (err) {
      setTestMsg({ ok: false, text: err instanceof ApiError ? err.message : t('accountPage.sendFailed') });
    } finally {
      setBusyTest(false);
    }
  }

  return (
    <div className="w-full space-y-6 p-6">
      <div>
        <h1 className="text-lg font-semibold text-strong">{t('accountPage.account')}</h1>
        <p className="text-sm text-muted">{me?.name} · {me?.email ?? t('accountPage.noEmailOnFile')} {me?.isAdmin && `· ${t('accountPage.administrator')}`}</p>
      </div>

      {/* Test email */}
      <section className="rounded-xl border line surface-card p-5">
        <div className="mb-1 flex items-center gap-2">
          <Mail className="size-4 text-brand-500" />
          <h2 className="text-sm font-semibold text-strong">{t('accountPage.testEmailDelivery')}</h2>
        </div>
        <p className="mb-3 text-[13px] text-muted">{t('accountPage.testEmailDesc')}</p>
        <Button variant="secondary" loading={busyTest} onClick={sendTest} disabled={!me?.email}>{t('accountPage.sendTestEmail')}</Button>
        {testMsg && (
          <p className={`mt-3 rounded-lg px-3 py-2 text-[13px] ${testMsg.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>{testMsg.text}</p>
        )}
      </section>

      {/* Password */}
      <section className="rounded-xl border line surface-card p-5">
        <div className="mb-1 flex items-center gap-2">
          <KeyRound className="size-4 text-brand-500" />
          <h2 className="text-sm font-semibold text-strong">{t('accountPage.changePassword')}</h2>
        </div>
        <p className="mb-3 text-[13px] text-muted">
          {t('accountPage.changePasswordDesc')}
        </p>
        <form onSubmit={submitPassword} className="max-w-sm space-y-3">
          <input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required
            placeholder={t('accountPage.currentPasswordPlaceholder')} autoComplete="current-password"
            className="w-full rounded-lg border line surface-card px-3 py-2 text-sm text-body focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
          <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={12}
            placeholder={t('accountPage.newPasswordPlaceholder')} autoComplete="new-password"
            className="w-full rounded-lg border line surface-card px-3 py-2 text-sm text-body focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
          <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required minLength={12}
            placeholder={t('accountPage.confirmNewPasswordPlaceholder')} autoComplete="new-password"
            className="w-full rounded-lg border line surface-card px-3 py-2 text-sm text-body focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
          <Button type="submit" variant="primary" loading={busyPw}>{t('accountPage.changePassword')}</Button>
        </form>
        {pwMsg && (
          <p className={`mt-3 rounded-lg px-3 py-2 text-[13px] ${pwMsg.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`} role={pwMsg.ok ? 'status' : 'alert'}>{pwMsg.text}</p>
        )}
      </section>

      {/* Sign out everywhere */}
      <section className="rounded-xl border line surface-card p-5">
        <div className="mb-1 flex items-center gap-2">
          <LogOut className="size-4 text-brand-500" />
          <h2 className="text-sm font-semibold text-strong">{t('accountPage.signOutEverywhereElse')}</h2>
        </div>
        <p className="mb-3 text-[13px] text-muted">
          {t('accountPage.signOutEverywhereDesc')}
        </p>
        <Button variant="secondary" loading={busySignOut} onClick={signOutEverywhere}>{t('accountPage.signOutOtherDevices')}</Button>
        {signOutMsg && (
          <p className={`mt-3 rounded-lg px-3 py-2 text-[13px] ${signOutMsg.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`} role="status">{signOutMsg.text}</p>
        )}
      </section>

      {/* Share link (admin) */}
      {me?.isAdmin && (
        <section className="rounded-xl border line surface-card p-5">
          <div className="mb-1 flex items-center gap-2">
            <Link2 className="size-4 text-brand-500" />
            <h2 className="text-sm font-semibold text-strong">{t('accountPage.instantAccessLink')}</h2>
          </div>
          <p className="mb-3 text-[13px] text-muted">
            {t('accountPage.instantAccessPrefix')} <strong>{me.username}</strong> {t('accountPage.instantAccessSuffix')}
          </p>
          <Button variant="primary" loading={busyShare} onClick={makeShareLink}>{t('accountPage.generateAccessLink')}</Button>
          {shareLink && (
            <div className="mt-3 flex items-center gap-2 rounded-lg surface-sunken p-2">
              <code className="min-w-0 flex-1 truncate px-1 text-[12px] text-body">{shareLink}</code>
              <Button variant="ghost" className="h-8 shrink-0" onClick={copy}>
                <Copy className="size-3.5" /> {copied ? t('accountPage.copied') : t('accountPage.copy')}
              </Button>
            </div>
          )}
          <p className="mt-2 flex items-center gap-1.5 text-[12px] text-faint">
            <ShieldCheck className="size-3.5" /> {t('accountPage.shareWarning')}
          </p>
        </section>
      )}
    </div>
  );
}
